import type { FetchBaseQueryMeta } from '@reduxjs/toolkit/query';
import { ApiResponseType } from '@types';
import { toasterApi } from '@ui';
import { actions, ActionType } from '../constants/appActions';

export type ApiStatusType = {
  status: keyof ApiResponseType.ApiCodes;
  code: ApiResponseType.ApiCodes[keyof ApiResponseType.ApiCodes] | string;
  action: (typeof actions)[keyof typeof actions];
  actionType: (typeof ActionType)[keyof typeof ActionType];
  message: string;
  detail: string;
  showDetails: string | string[] | undefined;
};

// Switch-based API response mapping function
export const getApiResponseMapping = <T extends keyof ApiResponseType.ApiCodes>(
  status: T,
  subCode?: ApiResponseType.ApiCodes[T] | string,
): ApiStatusType => {
  switch (status) {
    case 200:
      switch (subCode) {
        case 'MFARequired':
          return {
            status: 200,
            code: 'MFARequired',
            action: actions.mfaRequired,
            actionType: ActionType.warning,
            message: 'MFA Required',
            detail:
              'MFA is required to access the application. Please verify your account by entering the code from your authenticator app.',
            showDetails: undefined,
          };
        case 'Success':
        default:
          return {
            status: 200,
            code: 'Success',
            action: actions.success,
            actionType: ActionType.success,
            message: 'Success',
            detail: 'The request was successful.',
            showDetails: undefined,
          };
      }

    case 201:
      switch (subCode) {
        case 'Success':
        case 'Created':
        default:
          return {
            status: 201,
            code: 'Created',
            action: actions.created,
            actionType: ActionType.success,
            message: 'Created',
            detail: 'The request was successful.',
            showDetails: undefined,
          };
      }

    case 202:
      switch (subCode) {
        case 'Accepted':
        default:
          return {
            status: 202,
            code: 'Accepted',
            action: actions.success,
            actionType: ActionType.success,
            message: 'Accepted',
            detail:
              'The request was successful and the server will process the request asynchronously.',
            showDetails: undefined,
          };
      }

    case 204:
      switch (subCode) {
        case 'NoContent':
        default:
          return {
            status: 204,
            code: 'NoContent',
            action: actions.success,
            actionType: ActionType.success,
            message: 'No Content',
            detail:
              'The request was successful and the server did not return any content.',
            showDetails: undefined,
          };
      }

    case 206:
      switch (subCode) {
        case 'PartialContent':
        default:
          return {
            status: 206,
            code: 'PartialContent',
            action: actions.warning,
            actionType: ActionType.warning,
            message: 'Partial Content',
            detail:
              'The request was successful, but some records were not updated. Please check the details for more information.',
            showDetails: undefined,
          };
      }

    case 207:
      switch (subCode) {
        case 'PartialSuccess':
        default:
          return {
            status: 207,
            code: 'PartialSuccess',
            action: actions.warning,
            actionType: ActionType.warning,
            message: 'Partial Success',
            detail:
              'The request was successful, but some records were not updated. Please check the details for more information.',
            showDetails: undefined,
          };
      }

    case 301:
      switch (subCode) {
        case 'MovedPermanentlyError':
        default:
          return {
            status: 301,
            code: 'MovedPermanentlyError',
            action: actions.warning,
            actionType: ActionType.warning,
            message: 'Moved Permanently',
            detail:
              'The requested resource has been permanently moved to a new location.',
            showDetails: undefined,
          };
      }

    case 302:
      switch (subCode) {
        case 'FoundError':
        default:
          return {
            status: 302,
            code: 'FoundError',
            action: actions.warning,
            actionType: ActionType.warning,
            message: 'Found',
            detail:
              'The requested resource has been temporarily moved to a new location.',
            showDetails: undefined,
          };
      }

    case 303:
      switch (subCode) {
        case 'SeeOtherError':
        default:
          return {
            status: 303,
            code: 'SeeOtherError',
            action: actions.warning,
            actionType: ActionType.warning,
            message: 'See Other',
            detail: 'The requested resource is available at another location.',
            showDetails: undefined,
          };
      }

    case 304:
      switch (subCode) {
        case 'NotModifiedError':
        default:
          return {
            status: 304,
            code: 'NotModifiedError',
            action: actions.warning,
            actionType: ActionType.warning,
            message: 'Not Modified',
            detail:
              'The requested resource has not been modified since the last request.',
            showDetails: undefined,
          };
      }

    case 307:
      switch (subCode) {
        case 'TemporaryRedirectError':
        default:
          return {
            status: 307,
            code: 'TemporaryRedirectError',
            action: actions.warning,
            actionType: ActionType.warning,
            message: 'Temporary Redirect',
            detail:
              'The requested resource is available at another location for a temporary period.',
            showDetails: undefined,
          };
      }

    case 308:
      switch (subCode) {
        case 'PermanentRedirectError':
        default:
          return {
            status: 308,
            code: 'PermanentRedirectError',
            action: actions.warning,
            actionType: ActionType.warning,
            message: 'Permanent Redirect',
            detail:
              'The requested resource has been permanently moved to a new location.',
            showDetails: undefined,
          };
      }

    case 400:
      switch (subCode) {
        case 'BadRequestError':
        default:
          return {
            status: 400,
            code: 'BadRequestError',
            action: actions.error,
            actionType: ActionType.error,
            message: 'Bad Request',
            detail:
              'The request could not be understood or was missing required parameters.',
            showDetails: undefined,
          };
      }

    case 401:
      switch (subCode) {
        case 'InvalidOTPError':
          return {
            status: 401,
            code: 'InvalidOTPError',
            action: actions.error,
            actionType: ActionType.error,
            message: 'Invalid OTP',
            detail: 'The OTP is invalid or expired. Please try again.',
            showDetails: undefined,
          };
        case 'UnauthorizedError':
          return {
            status: 401,
            code: 'UnauthorizedError',
            action: actions.error,
            actionType: ActionType.error,
            message: 'Unauthorized',
            detail:
              'Authentication is required and has failed or has not been provided.',
            showDetails: undefined,
          };
        default:
          return {
            status: 401,
            code: 'AuthenticationError',
            action: actions.logoutCurrent,
            actionType: ActionType.error,
            message: 'Authentication Error',
            detail: 'An authentication error occurred. Please try again.',
            showDetails: undefined,
          };
      }

    case 403:
      switch (subCode) {
        case 'DashboardDataAccessDenied':
          return {
            status: 403,
            code: 'DashboardDataAccessDenied',
            action: actions.error,
            actionType: ActionType.warning,
            message: 'Dashboard Data Access Denied',
            detail: 'You do not have permission to access this dashboard data.',
            showDetails: undefined,
          };
        case 'ForbiddenError':
          return {
            status: 403,
            code: 'ForbiddenError',
            action: actions.resetApp,
            actionType: ActionType.error,
            message: 'Forbidden',
            detail:
              'The server understood the request but refuses to authorize it.',
            showDetails: undefined,
          };
        case 'PermissionError':
        default:
          return {
            status: 403,
            code: 'PermissionError',
            action: actions.error,
            actionType: ActionType.error,
            message: 'Permission Error',
            detail: 'You do not have permission to access this resource.',
            showDetails: undefined,
          };
      }

    case 404:
      switch (subCode) {
        case 'NotFoundError':
        default:
          return {
            status: 404,
            code: 'NotFoundError',
            action: actions.error,
            actionType: ActionType.error,
            message: 'Not Found',
            detail: 'The requested resource could not be found on the server.',
            showDetails: undefined,
          };
      }

    case 405:
      switch (subCode) {
        case 'MethodNotAllowedError':
        default:
          return {
            status: 405,
            code: 'MethodNotAllowedError',
            action: actions.error,
            actionType: ActionType.error,
            message: 'Method Not Allowed',
            detail: 'The HTTP method used is not allowed for this resource.',
            showDetails: undefined,
          };
      }

    case 406:
      switch (subCode) {
        case 'NotAcceptableError':
        default:
          return {
            status: 406,
            code: 'NotAcceptableError',
            action: actions.error,
            actionType: ActionType.error,
            message: 'Not Acceptable',
            detail:
              'The requested resource is not available in a format that would be acceptable.',
            showDetails: undefined,
          };
      }

    case 409:
      switch (subCode) {
        case 'AlreadyExistsError':
          return {
            status: 409,
            code: 'AlreadyExistsError',
            action: actions.error,
            actionType: ActionType.error,
            message: 'Already Exists',
            detail:
              'The resource already exists. Please try a different resource name.',
            showDetails: undefined,
          };
        case 'ConflictError':
        default:
          return {
            status: 409,
            code: 'ConflictError',
            action: actions.error,
            actionType: ActionType.warning,
            message: 'Conflict',
            detail:
              'The request could not be completed due to a conflict with the current state of the resource.',
            showDetails: undefined,
          };
      }

    case 410:
      switch (subCode) {
        case 'GoneError':
        default:
          return {
            status: 410,
            code: 'GoneError',
            action: actions.redirect,
            actionType: ActionType.error,
            message: 'Gone',
            detail:
              'The requested resource is no longer available at the server.',
            showDetails: undefined,
          };
      }

    case 411:
      switch (subCode) {
        case 'LengthRequiredError':
        default:
          return {
            status: 411,
            code: 'LengthRequiredError',
            action: actions.error,
            actionType: ActionType.error,
            message: 'Length Required',
            detail:
              'The request did not specify the length of its content, which is required by the requested resource.',
            showDetails: undefined,
          };
      }

    case 412:
      switch (subCode) {
        case 'PreconditionFailedError':
        default:
          return {
            status: 412,
            code: 'PreconditionFailedError',
            action: actions.refresh,
            actionType: ActionType.error,
            message: 'Precondition Failed',
            detail:
              'The precondition given in one or more of the request-header fields evaluated to false.',
            showDetails: undefined,
          };
      }

    case 413:
      switch (subCode) {
        case 'PayloadTooLargeError':
        default:
          return {
            status: 413,
            code: 'PayloadTooLargeError',
            action: actions.error,
            actionType: ActionType.error,
            message: 'Payload Too Large',
            detail:
              'The request entity is larger than the server is willing or able to process.',
            showDetails: undefined,
          };
      }

    case 415:
      switch (subCode) {
        case 'UnsupportedMediaTypeError':
        default:
          return {
            status: 415,
            code: 'UnsupportedMediaTypeError',
            action: actions.error,
            actionType: ActionType.error,
            message: 'Unsupported Media Type',
            detail:
              'The media type of the requested data is not supported by the server.',
            showDetails: undefined,
          };
      }

    case 422:
      switch (subCode) {
        case 'UnprocessableEntityError':
        default:
          return {
            status: 422,
            code: 'UnprocessableEntityError',
            action: actions.refresh,
            actionType: ActionType.error,
            message: 'Unprocessable Entity',
            detail:
              'The request was well-formed but was unable to be followed due to semantic errors.',
            showDetails: undefined,
          };
      }

    case 429:
      switch (subCode) {
        case 'TooManyRequestsError':
        default:
          return {
            status: 429,
            code: 'TooManyRequestsError',
            action: actions.timeout,
            actionType: ActionType.error,
            message: 'Too Many Requests',
            detail:
              'The user has sent too many requests in a given amount of time.',
            showDetails: undefined,
          };
      }

    case 500:
      switch (subCode) {
        case 'InternalServerError':
        default:
          return {
            status: 500,
            code: 'InternalServerError',
            action: actions.error,
            actionType: ActionType.error,
            message: 'Internal Server Error',
            detail:
              'An internal server error occurred while processing the request.',
            showDetails: undefined,
          };
      }

    case 501:
      switch (subCode) {
        case 'NotImplementedError':
        default:
          return {
            status: 501,
            code: 'NotImplementedError',
            action: actions.error,
            actionType: ActionType.error,
            message: 'Not Implemented',
            detail:
              'The server does not support the functionality required to fulfill the request.',
            showDetails: undefined,
          };
      }

    case 502:
      switch (subCode) {
        case 'BadGatewayError':
        default:
          return {
            status: 502,
            code: 'BadGatewayError',
            action: actions.error,
            actionType: ActionType.error,
            message: 'Bad Gateway',
            detail:
              'The server received an invalid response from an upstream server.',
            showDetails: undefined,
          };
      }

    case 503:
      switch (subCode) {
        case 'ServiceUnavailableError':
        default:
          return {
            status: 503,
            code: 'ServiceUnavailableError',
            action: actions.resetApp,
            actionType: ActionType.error,
            message: 'Service Unavailable',
            detail:
              'The server is temporarily unable to handle the request due to maintenance or overload.',
            showDetails: undefined,
          };
      }

    case 504:
      switch (subCode) {
        case 'GatewayTimeoutError':
        default:
          return {
            status: 504,
            code: 'GatewayTimeoutError',
            action: actions.error,
            actionType: ActionType.error,
            message: 'Gateway Timeout',
            detail:
              'The server did not receive a timely response from an upstream server.',
            showDetails: undefined,
          };
      }

    case 505:
      switch (subCode) {
        case 'HttpVersionNotSupportedError':
        default:
          return {
            status: 505,
            code: 'HttpVersionNotSupportedError',
            action: actions.error,
            actionType: ActionType.error,
            message: 'HTTP Version Not Supported',
            detail:
              'The server does not support the HTTP protocol version used in the request.',
            showDetails: undefined,
          };
      }

    case 0:
    default:
      switch (subCode) {
        case 'NetworkError':
          return {
            status: 0,
            code: 'NetworkError',
            action: actions.retry,
            actionType: ActionType.error,
            message: 'Network Error',
            detail:
              'Unable to connect to server. Please check your connection and try again later.',
            showDetails: undefined,
          };
        case 'PermissionError':
          return {
            status: 0,
            code: 'PermissionError',
            action: actions.logoutCurrent,
            actionType: ActionType.error,
            message: 'Permission Error',
            detail: 'You do not have permission to access this resource.',
            showDetails: undefined,
          };
        case 'UnknownError':
        default:
          return {
            status: 0,
            code: 'UnknownError',
            action: actions.error,
            actionType: ActionType.error,
            message: 'Unknown Error',
            detail: 'An unknown error occurred. Please try again later.',
            showDetails: undefined,
          };
      }
  }
};

export const responseHasDataDefault = (
  method: ApiResponseType.HTTPRequest,
  status: ApiResponseType.ApiStatus,
): boolean => {
  if (method === 'HEAD') return false;
  if (method === 'CONNECT' && status === 200) return false;
  if (status === 204) return false;
  return true;
};

export const meetsExpectedHttpStatusDefault = (
  method: ApiResponseType.HTTPRequest,
  status: ApiResponseType.ApiStatus,
): boolean => {
  return (
    ApiResponseType.SuccessStatusByMethod[
      method as ApiResponseType.HTTPRequest
    ].includes(status as any) || false
  );
};

export type TransformApiResponseArgs = {
  response: ApiResponseType.ApiResponse<any>;
  meta: FetchBaseQueryMeta | undefined;
  arg: any;
  expectedHttpStatuses?: (typeof ApiResponseType.HTTPSuccessStatus)[number][];
  hasData?: boolean;
};

export type TransformApiResponseResult = <T>(
  args: TransformApiResponseArgs,
) => T | undefined;

export const transformApiResponse = <T>({
  response,
  meta,
  expectedHttpStatuses,
  hasData,
}: TransformApiResponseArgs) => {
  const requestType = meta?.request.method;
  const responseOK: boolean = meta?.response?.ok || false;
  const responseStatus = meta?.response?.status;

  const meetsExpectedHttpStatus: boolean = expectedHttpStatuses
    ? responseStatus !== undefined &&
      expectedHttpStatuses.includes(responseStatus as any)
    : requestType
      ? meetsExpectedHttpStatusDefault(
          requestType as any,
          responseStatus as any,
        )
      : false;

  const responseHasData: boolean = hasData
    ? responseOK && meetsExpectedHttpStatus && response && 'data' in response
    : responseOK &&
        meetsExpectedHttpStatus &&
        responseHasDataDefault(requestType as any, responseStatus as any)
      ? true
      : false;

  const defaultResponseMapping = getApiResponseMapping(
    responseStatus as any,
    response && 'data' in response && 'code' in response.data
      ? response.data.code
      : undefined,
  );

  if (
    responseOK &&
    meetsExpectedHttpStatus &&
    requestType !== 'GET' &&
    responseStatus !== 207
  ) {
    toasterApi({
      type: defaultResponseMapping.actionType,
      status: defaultResponseMapping.status,
      code: defaultResponseMapping.code,
      message: defaultResponseMapping.message,
      detail: defaultResponseMapping.detail,
      showDetails: defaultResponseMapping.showDetails,
    });
  }

  if (response && 'data' in response && responseHasData) {
    return response.data as T;
  }
  return undefined;
};
